/**
 * F-005 US3 — historical impact-snapshot view. Read-only render of a
 * specific persisted snapshot by id (no input panel). The server-computed
 * breakdown includes the real affordability factor. A version-drift banner
 * fires when the snapshot's stamps differ from the response's
 * currentVersions (FR-051). Unknown / cross-tenant / wrong-project ids
 * resolve to the not-found card (FR-053 combined-key lookup).
 */
import { createFileRoute, Link, useParams } from '@tanstack/react-router';
import { Spinner } from '@/components/atoms/Spinner';
import { useImpactById } from '@/lib/api/impactApi';
import { ProgressRing } from '@/features/impact/ProgressRing';
import { ImpactBreakdownPanel } from '@/features/impact/ImpactBreakdownPanel';
import type { LocalFactorContribution } from '@/features/impact/useImpactScoreLocal';

export const Route = createFileRoute('/projects/$projectId/impact/$snapshotId')({
  component: HistoricalImpactPage,
});

function HistoricalImpactPage() {
  const { projectId, snapshotId } = useParams({ strict: false }) as {
    projectId?: string;
    snapshotId?: string;
  };
  const q = useImpactById(projectId, snapshotId);

  if (!projectId || !snapshotId) return null;

  if (q.isLoading) {
    return <div className="flex h-64 items-center justify-center"><Spinner aria-label="Loading impact snapshot" /></div>;
  }
  if (q.isError || !q.data) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <h1 className="font-display text-title-sm">Snapshot not found.</h1>
        <p className="mt-2 text-body-sm text-muted-foreground">
          This snapshot may have been pruned beyond the retention window, or it may belong to another project.
        </p>
        <BackLink projectId={projectId} />
      </div>
    );
  }

  const { snapshot, currentVersions } = q.data;
  const drift = snapshot.weightsVersion !== currentVersions.weightsVersion;

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <BackLink projectId={projectId} />
        <span className="rounded-full bg-muted px-3 py-1 text-caption uppercase tracking-wider text-muted-foreground">
          Historical snapshot
        </span>
      </div>

      {drift && (
        <div className="rounded-md border border-warning bg-warning/10 p-3 text-body-sm text-warning">
          Scored under weights v{snapshot.weightsVersion}; the current formula is v{currentVersions.weightsVersion}.
          Re-open the Impact tab and re-save to score under the current formula.
        </div>
      )}

      <div className="flex flex-col items-center gap-2 rounded-md border border-border bg-card p-6">
        <ProgressRing score={snapshot.overallScore} tier={snapshot.tier} />
        <p className="text-caption text-muted-foreground">
          {new Date(snapshot.computedAt).toLocaleString()} · Formula v{snapshot.formulaVersion} · Weights v{snapshot.weightsVersion}
        </p>
      </div>

      <div className="rounded-md border border-border bg-card p-6">
        <ImpactBreakdownPanel contributions={snapshot.breakdown.contributions as LocalFactorContribution[]} />
      </div>
    </div>
  );
}

function BackLink({ projectId }: { projectId: string }) {
  return (
    <Link
      to="/projects/$projectId/workspace/$tab"
      params={{ projectId, tab: 'impact' }}
      className="text-caption text-primary hover:underline"
    >
      ← Back to the Impact tab
    </Link>
  );
}
